/**
 * RichTextEditor — the shared TipTap-based editor used across BriteHub
 * (Events, Courses, Learning Library, News, Companies, BriteLab, Members,
 * Partners, Forum posts). Emits HTML on change; renders paragraphs, headings,
 * lists, blockquotes, links, underline, colour and highlight.
 */
import React, { useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading2,
  List, ListOrdered, Quote, Link as LinkIcon, Highlighter,
  Palette, Undo2, Redo2, Type,
} from 'lucide-react';

// BriteHub brand palette — matches tailwind.config.js.
const BRAND_COLOURS = [
  { label: 'Default', value: '' },
  { label: 'Navy', value: '#0A2540' },
  { label: 'Sage', value: '#568259' },
  { label: 'Blue', value: '#8EC5E8' },
  { label: 'Slate', value: '#475569' },
  { label: 'Black', value: '#000000' },
  { label: 'Rose (warning)', value: '#e11d48' },
];

const HIGHLIGHT_COLOURS = [
  { label: 'None', value: '' },
  { label: 'Sage', value: '#dbe8dc' },
  { label: 'Blue', value: '#dbeef9' },
  { label: 'Amber', value: '#fef3c7' },
  { label: 'Rose', value: '#fee2e2' },
];

function ToolbarBtn({ onClick, active, disabled, title, testId, children }) {
  return (
    <button
      type="button"
      // Prevent the contenteditable losing focus BEFORE onClick fires — that
      // otherwise swallows the first keystroke after a toolbar click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      disabled={disabled}
      data-testid={testId}
      className={`
        h-7 px-1.5 rounded flex items-center justify-center transition-colors
        ${active
          ? 'bg-brand-navy text-white'
          : 'text-slate-600 hover:bg-slate-100 hover:text-brand-navy'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {children}
    </button>
  );
}

export function RichTextToolbar({ editor, testIdPrefix = 'rte' }) {
  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL (leave empty to remove link):', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({
      href: url.startsWith('http') ? url : `https://${url}`,
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    }).run();
  }, [editor]);
  if (!editor) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 rounded-t-md px-1.5 py-1"
      data-testid={`${testIdPrefix}-toolbar`}
    >
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')} title="Bold (Ctrl+B)"
        testId={`${testIdPrefix}-bold`}>
        <Bold className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')} title="Italic (Ctrl+I)"
        testId={`${testIdPrefix}-italic`}>
        <Italic className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')} title="Underline (Ctrl+U)"
        testId={`${testIdPrefix}-underline`}>
        <UnderlineIcon className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <span className="w-px h-4 bg-slate-300 mx-0.5" />
      <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })} title="Heading"
        testId={`${testIdPrefix}-h2`}>
        <Heading2 className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().setParagraph().run()}
        active={editor.isActive('paragraph')} title="Paragraph"
        testId={`${testIdPrefix}-p`}>
        <Type className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')} title="Bullet list"
        testId={`${testIdPrefix}-ul`}>
        <List className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')} title="Numbered list"
        testId={`${testIdPrefix}-ol`}>
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')} title="Quote"
        testId={`${testIdPrefix}-quote`}>
        <Quote className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <span className="w-px h-4 bg-slate-300 mx-0.5" />
      <ToolbarBtn onClick={setLink} active={editor.isActive('link')} title="Link"
        testId={`${testIdPrefix}-link`}>
        <LinkIcon className="h-3.5 w-3.5" />
      </ToolbarBtn>

      {/* Text colour */}
      <ColourMenu
        icon={<Palette className="h-3.5 w-3.5" />}
        title="Text colour"
        colours={BRAND_COLOURS}
        onPick={(c) => {
          if (c) editor.chain().focus().setColor(c).run();
          else editor.chain().focus().unsetColor().run();
        }}
        active={!!editor.getAttributes('textStyle').color}
        testId={`${testIdPrefix}-color`}
      />
      {/* Highlight */}
      <ColourMenu
        icon={<Highlighter className="h-3.5 w-3.5" />}
        title="Highlight"
        colours={HIGHLIGHT_COLOURS}
        onPick={(c) => {
          if (c) editor.chain().focus().setHighlight({ color: c }).run();
          else editor.chain().focus().unsetHighlight().run();
        }}
        active={editor.isActive('highlight')}
        testId={`${testIdPrefix}-highlight`}
      />

      <span className="ml-auto flex items-center gap-0.5">
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()} title="Undo"
          testId={`${testIdPrefix}-undo`}>
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()} title="Redo"
          testId={`${testIdPrefix}-redo`}>
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </span>
    </div>
  );
}

function ColourMenu({ icon, title, colours, onPick, active, testId }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <ToolbarBtn onClick={() => setOpen(v => !v)} active={active} title={title} testId={testId}>
        {icon}
      </ToolbarBtn>
      {open && (
        <div className="absolute z-20 mt-1 top-full left-0 bg-white border border-slate-200 rounded-md shadow-lg p-1.5 w-40"
             data-testid={`${testId}-menu`}>
          {colours.map(c => (
            <button
              type="button"
              key={c.value || 'none'}
              onClick={() => { onPick(c.value); setOpen(false); }}
              data-testid={`${testId}-opt-${(c.label || 'none').toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center gap-2 w-full px-1.5 py-1 text-xs hover:bg-slate-50 rounded"
            >
              <span
                className="inline-block h-4 w-4 rounded border border-slate-200"
                style={{ background: c.value || 'transparent' }}
              />
              <span className="text-slate-700">{c.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Props
 * - value: current HTML string.
 * - onChange: (html) => void
 * - placeholder: string shown when empty.
 * - minHeight: e.g. '10rem'; controls editor body min-height.
 * - testId: root data-testid for automation.
 * - disabled: bool
 */
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Start typing…',
  minHeight = '8rem',
  testId = 'rte',
  disabled = false,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        link: false,
        underline: false,
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Placeholder.configure({ placeholder }),
    ],
    editable: !disabled,
    content: value || '',
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      // TipTap emits `<p></p>` for an empty doc — normalise that to empty
      // string so consumers can treat "no content" uniformly.
      const trimmed = html === '<p></p>' ? '' : html;
      onChange?.(trimmed);
    },
  });

  // Sync external value changes into the editor (e.g. form reset).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && (incoming !== '' || current !== '<p></p>')) {
      editor.commands.setContent(incoming, false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <div className="rich-text-editor border border-slate-300 rounded-md bg-white overflow-hidden" data-testid={testId}>
      <RichTextToolbar editor={editor} testIdPrefix={testId} />
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-3 py-2 focus:outline-none"
        style={{ minHeight }}
        data-testid={`${testId}-body`}
      />
    </div>
  );
}
